// First-person controller: pointer-lock mouse-look + WASD with AABB collision.
// No three.js add-ons needed — we drive the camera Euler directly. Sitting locks
// movement and parks the camera at a seat anchor.

import THREE from "./three.js";
import type { PerspectiveCamera } from "three";
import type { AABB, Seat } from "./world/layout.js";

const EYE = 1.6; // standing eye height
const RADIUS = 0.28; // player collision radius
const SPEED = 2.4; // m/s walk
const PITCH_LIMIT = Math.PI / 2 - 0.05;

export class Controller {
  yaw = 0;
  pitch = 0;
  seated: Seat | null = null;
  locked = false;
  enabled = false;

  // On pointer-lock engage the browser can deliver one huge synthetic mouse
  // delta (cursor-to-centre). We swallow the first move after locking so the
  // view doesn't snap/spin.
  private swallowNext = false;

  // Perturbing-effect state, driven by the entity manager while a presence is
  // here (mirror of the spectator effects). invert → look axes flip; timeScale
  // drags movement out; fxRoll/fxJx/fxJy are camera disturbance applied on sync.
  invert = false;
  timeScale = 1;
  fxRoll = 0;
  fxJx = 0;
  fxJy = 0;

  private keys = new Set<string>();
  private pos = new THREE.Vector3(3, EYE, 3.5);

  constructor(
    private camera: PerspectiveCamera,
    private dom: HTMLElement,
    private colliders: AABB[],
  ) {
    this.camera.rotation.order = "YXZ";
    document.addEventListener("keydown", (e) => this.onKey(e, true));
    document.addEventListener("keyup", (e) => this.onKey(e, false));
    document.addEventListener("mousemove", (e) => this.onMouse(e));
    document.addEventListener("pointerlockchange", () => {
      const nowLocked = document.pointerLockElement === this.dom;
      if (nowLocked && !this.locked) this.swallowNext = true;
      this.locked = nowLocked;
    });
  }

  spawn(pos: THREE.Vector3, yaw: number) {
    this.pos.copy(pos);
    this.pos.y = EYE;
    this.yaw = yaw;
    this.pitch = 0;
    this.seated = null;
    this.syncCamera();
  }

  requestLock() {
    this.dom.requestPointerLock?.();
  }

  setEnabled(on: boolean) {
    this.enabled = on;
    if (!on) this.keys.clear();
  }

  // ── Seating ──
  sit(seat: Seat) {
    this.seated = seat;
    this.pos.set(seat.pos.x, seat.pos.y, seat.pos.z);
    this.yaw = seat.yaw;
    this.pitch = 0;
    this.syncCamera();
  }
  stand() {
    if (!this.seated) return;
    // Step forward in the direction the SEAT faces, so we step into open floor
    // (away from the bench) instead of clipping into it.
    const s = this.seated;
    const fx = -Math.sin(s.yaw);
    const fz = -Math.cos(s.yaw);
    this.pos.set(s.pos.x + fx * 0.6, EYE, s.pos.z + fz * 0.6);
    this.seated = null;
    this.syncCamera();
  }
  get isSeated() {
    return this.seated !== null;
  }

  // ── Per-frame ──
  update(dt: number) {
    if (!this.enabled || this.seated) {
      this.syncCamera();
      return;
    }
    let fx = 0;
    let fz = 0;
    if (this.keys.has("KeyW")) fz -= 1;
    if (this.keys.has("KeyS")) fz += 1;
    if (this.keys.has("KeyA")) fx -= 1;
    if (this.keys.has("KeyD")) fx += 1;
    if (fx === 0 && fz === 0) {
      this.syncCamera();
      return;
    }
    const len = Math.hypot(fx, fz);
    fx /= len;
    fz /= len;

    // Rotate the input by yaw. Forward (-z in world) = (-sin yaw, -cos yaw).
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    const worldX = fx * cos - fz * sin;
    const worldZ = fx * sin + fz * cos;

    // Sub-step the movement so we can NEVER tunnel through a thin wall, even at
    // a low frame rate. Each sub-step is at most ~3.5cm and is resolved per-axis
    // (so we slide along walls instead of sticking).
    const dist = SPEED * dt * this.timeScale;
    const sub = Math.max(1, Math.ceil(dist / 0.035));
    const sx = (worldX * dist) / sub;
    const sz = (worldZ * dist) / sub;
    for (let i = 0; i < sub; i++) {
      const nx = this.pos.x + sx;
      if (!this.collides(nx, this.pos.z)) this.pos.x = nx;
      const nz = this.pos.z + sz;
      if (!this.collides(this.pos.x, nz)) this.pos.z = nz;
    }

    this.syncCamera();
  }

  position(): THREE.Vector3 {
    return this.pos;
  }
  forward(): THREE.Vector3 {
    return new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
  }

  private collides(x: number, z: number): boolean {
    for (const c of this.colliders) {
      if (
        x > c.minX - RADIUS &&
        x < c.maxX + RADIUS &&
        z > c.minZ - RADIUS &&
        z < c.maxZ + RADIUS
      ) {
        return true;
      }
    }
    return false;
  }

  private syncCamera() {
    this.camera.position.set(this.pos.x + this.fxJx, this.pos.y + this.fxJy, this.pos.z);
    this.camera.rotation.set(this.pitch, this.yaw, this.fxRoll, "YXZ");
  }

  private onKey(e: KeyboardEvent, down: boolean) {
    if (down) this.keys.add(e.code);
    else this.keys.delete(e.code);
  }

  private onMouse(e: MouseEvent) {
    if (!this.locked || !this.enabled) return;
    if (this.swallowNext) { this.swallowNext = false; return; }
    // Guard against undefined deltas (→ NaN, which would corrupt the camera for
    // good and make the view spin) and clamp absurd single-frame spikes.
    let dx = e.movementX;
    let dy = e.movementY;
    if (!Number.isFinite(dx)) dx = 0;
    if (!Number.isFinite(dy)) dy = 0;
    const CAP = 120; // px per event; kills jolts from focus/lock glitches
    dx = Math.max(-CAP, Math.min(CAP, dx));
    dy = Math.max(-CAP, Math.min(CAP, dy));
    const s = 0.0022;
    // "invert" effect flips the look axes — your hands no longer obey you.
    const sign = this.invert ? 1 : -1;
    this.yaw += sign * dx * s;
    this.pitch += sign * dy * s;
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch));
  }
}
